<script>
import PaginationController from '@/components/base-components/PaginationController.vue';

export default {
    components: {
        PaginationController,
    },
    data() {
        return {
            sortColumns: {
                dateSort: { field: 'upload_date', direction: 'desc' },
                countSort: { field: 'count', direction: null },
                cumulativeSort: { field: 'cumulative', direction: null },
            },
        };
    },
    created() {
        // Sync local sort state with store on initialization
        const storeSortBy = this.$store.getters.SYSTEM_ANALYTICS_SORT_BY;
        const storeSortOrder = this.$store.getters.SYSTEM_ANALYTICS_SORT_ORDER;

        // Find which column matches the store's sort field and update its direction
        Object.keys(this.sortColumns).forEach((key) => {
            const column = this.sortColumns[key];
            if (column.field === storeSortBy) {
                column.direction = storeSortOrder;
            } else {
                column.direction = null;
            }
        });
    },
    computed: {
        analytics() {
            return this.$store.getters.SYSTEM_ANALYTICS_DAILY_UPLOADS;
        },
        isLoading() {
            return this.$store.getters.SYSTEM_ANALYTICS_LOADING;
        },
        pagination() {
            return this.$store.getters.SYSTEM_ANALYTICS_PAGINATION;
        },
        currentPage() {
            return this.$store.getters.SYSTEM_ANALYTICS_CURRENT_PAGE;
        },
        totalPages() {
            return this.$store.getters.SYSTEM_ANALYTICS_TOTAL_PAGES;
        },
        maxPage() {
            return this.totalPages > 0 ? this.totalPages - 1 : 0;
        },
        displayRange() {
            const start =
                (this.pagination.page - 1) * this.pagination.perPage + 1;
            const end = Math.min(
                this.pagination.page * this.pagination.perPage,
                this.pagination.total
            );
            return `${start}-${end} of ${this.pagination.total}`;
        },
        chartData() {
            if (!this.analytics || this.analytics.length === 0) {
                return { dates: [], counts: [] };
            }

            const dates = this.analytics.map((item) =>
                this.formatDateForDisplay(item.upload_date)
            );
            const counts = this.analytics.map((item) => item.count || 0);

            return { dates, counts };
        },
    },
    methods: {
        handleSort(columnId) {
            const column = this.sortColumns[columnId];
            if (!column) return;

            // Cycle through: null -> asc -> desc -> null
            if (column.direction === null) {
                column.direction = 'asc';
            } else if (column.direction === 'asc') {
                column.direction = 'desc';
            } else {
                column.direction = null;
            }

            // Reset other columns (single-column sort)
            Object.keys(this.sortColumns).forEach((key) => {
                if (key !== columnId) {
                    this.sortColumns[key].direction = null;
                }
            });

            // Determine sort values
            const sortBy = column.direction ? column.field : 'upload_date';
            const sortOrder = column.direction || 'desc';

            this.$store.commit('SET_SYSTEM_ANALYTICS_SORT', {
                sortBy,
                sortOrder,
            });
            this.$store.commit('SET_SYSTEM_ANALYTICS_PAGE', 1);
            this.$store.dispatch('loadSystemAnalyticsDailyUploads');
        },

        getSortDirection(columnId) {
            return this.sortColumns[columnId]?.direction || null;
        },

        async handlePageChange(newPage) {
            await this.$store.dispatch('changeSystemAnalyticsPage', newPage);
        },

        formatDateForDisplay(dateStr) {
            // Handle undefined or null
            if (!dateStr) {
                return 'N/A';
            }
            // Parse YYYY-MM-DD format properly
            const [year, month, day] = dateStr.split('-');
            const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
            const options = { year: 'numeric', month: 'short', day: 'numeric' };
            return date.toLocaleDateString('en-US', options);
        },
    },
};
</script>

<template>
    <div class="analytics_list_container">
        <div v-if="isLoading" class="loading_container">
            <p>Loading analytics...</p>
        </div>

        <div v-else-if="analytics.length === 0" class="empty_container">
            <p>No data available for the selected date range</p>
        </div>

        <div v-else>
            <div class="table_scroll_container">
                <table>
                    <thead>
                        <tr>
                            <th
                                @click="handleSort('dateSort')"
                                :class="{
                                    sortable: true,
                                    sorted: getSortDirection('dateSort') !== null,
                                }"
                            >
                                Date
                                <span
                                    v-if="getSortDirection('dateSort') === 'asc'"
                                    class="sort_indicator"
                                >
                                    ▲
                                </span>
                                <span
                                    v-if="getSortDirection('dateSort') === 'desc'"
                                    class="sort_indicator"
                                >
                                    ▼
                                </span>
                            </th>
                            <th
                                @click="handleSort('countSort')"
                                :class="{
                                    sortable: true,
                                    sorted: getSortDirection('countSort') !== null,
                                }"
                            >
                                Daily Uploads
                                <span
                                    v-if="getSortDirection('countSort') === 'asc'"
                                    class="sort_indicator"
                                >
                                    ▲
                                </span>
                                <span
                                    v-if="getSortDirection('countSort') === 'desc'"
                                    class="sort_indicator"
                                >
                                    ▼
                                </span>
                            </th>
                            <th
                                @click="handleSort('cumulativeSort')"
                                :class="{
                                    sortable: true,
                                    sorted:
                                        getSortDirection('cumulativeSort') !==
                                        null,
                                }"
                            >
                                Cumulative
                                <span
                                    v-if="
                                        getSortDirection('cumulativeSort') ===
                                        'asc'
                                    "
                                    class="sort_indicator"
                                >
                                    ▲
                                </span>
                                <span
                                    v-if="
                                        getSortDirection('cumulativeSort') ===
                                        'desc'
                                    "
                                    class="sort_indicator"
                                >
                                    ▼
                                </span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(item, index) in analytics" :key="index">
                            <td>{{ formatDateForDisplay(item.upload_date) }}</td>
                            <td class="text_right">{{ item.count || 0 }}</td>
                            <td class="text_right">{{ item.cumulative || 0 }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Pagination -->
            <div v-if="analytics.length > 0 && totalPages > 1" class="pagination_container">
                <div class="pagination_info">
                    <span>Showing {{ displayRange }} records</span>
                </div>
                <PaginationController
                    :current-page="currentPage"
                    :total-pages="totalPages"
                    @page-change="handlePageChange"
                />
            </div>
        </div>
    </div>
</template>

<style scoped>
.analytics_list_container {
    padding: 20px;
}

.loading_container,
.empty_container {
    padding: 40px 20px;
    text-align: center;
    color: var(--text-color-secondary, #999);
}

.table_scroll_container {
    overflow-x: auto;
    overflow-y: visible;
    margin: 20px 0;
    border: 1px solid var(--border-color, #ddd);
    border-radius: 8px;
}

table {
    width: 100%;
    min-width: 600px;
    border-collapse: collapse;
}

th {
    background: var(--background-hover, #f5f5f5);
    padding: 12px 15px;
    text-align: left;
    font-weight: 600;
    color: var(--text-color, #000);
    border-bottom: 2px solid var(--border-color, #ddd);
    white-space: nowrap;
}

th.sortable {
    cursor: pointer;
    user-select: none;
    transition: background 0.2s ease;
}

th.sortable:hover {
    background: var(--main-color, #0066cc);
    color: white;
}

th.sorted {
    background: var(--main-color, #0066cc);
    color: white;
}

.sort_indicator {
    margin-left: 5px;
    font-size: 10px;
}

td {
    padding: 12px 15px;
    border-bottom: 1px solid var(--border-color, #ddd);
    color: var(--text-color, #000);
}

tr:hover {
    background: var(--row-hover-bg, #f9f9f9);
}

.text_right {
    text-align: right;
}

.pagination_container {
    margin-top: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 15px;
}

.pagination_info {
    color: var(--text-color-secondary, #666);
    font-size: 14px;
}
</style>
